import React, { FC, useEffect } from "react";
import {CommonState} from "../../../redux";
import {connect} from "react-redux";
import {AxiosError} from "axios";
import {handleAxiosError} from "../../../util/http";
import {loadAllAuditLogState, loadEntityAuditLogState} from "../../../redux/stateResponses/auditLogState";
import {useState} from "react";
import {bindActionCreators, Dispatch} from 'redux';
import {AuditLog, auditLogStore} from "../../../redux/entities/auditlog";
import {userStore} from "../../../redux/entities/user";
import AuditLogViewer from "./AuditLogViewer";
import {orderStore} from "../../../redux/entities/inventory/order";
import {orderItemStore} from "../../../redux/entities/inventory/joining/orderItem";
import {itemStore} from "../../../redux/entities/inventory/item";

type Props = { opened: boolean; onClose: () => void, entityId?: string } & ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

const LoaderComponent = (props: Props ) => {
  const { opened, onClose, entityId, auditLogs, entityAuditLogs, usersById, getUserById, getItemById, actions: { loadAuditData, loadEntityAuditData }} = props;
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [lastLoadedId, setLastLoadedId] = useState<string | undefined>(undefined);
  const [loadedAll, setLoadedAll] = useState(false);

  // Ensure loading state is cleared when drawer opens with data already available
  useEffect(() => {
    if (!opened) {
      // Reset loading/error when closed
      if (loading) setLoading(false);
      if (errorMessage) setErrorMessage(undefined);
      setLastLoadedId(undefined);
      setLoadedAll(false);
      return;
    }
    // If opened and we already have data, make sure loading is not shown
    const hasEntityLogs = entityId ? (entityId === lastLoadedId) : loadedAll;
    if (hasEntityLogs) {
      if (loading) setLoading(false);
    }
  }, [opened, entityId, lastLoadedId, loadedAll]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!opened) return;
      // Decide which dataset is authoritative based on entityId
      if (entityId) {
        if (entityId === lastLoadedId) return; // already attempted loading for this entity
        if (!active) return;
        setLoading(true);
        try {
          await loadEntityAuditData(entityId);
          if (active) setLastLoadedId(entityId);
        } catch (e: AxiosError | any) {
          setErrorMessage(handleAxiosError(e, { connectionMsg: 'Failed to load entity audit logs' }));
        } finally {
          if (active) setLoading(false);
        }
        return;
      }
      // Only auto-load all logs when no general items are present
      if (loadedAll) return;
      if (!active) return;
      setLoading(true);
      try {
        await loadAuditData();
        if (active) setLoadedAll(true);
      } catch (e: AxiosError | any) {
        setErrorMessage(handleAxiosError(e, { connectionMsg: 'Failed to load audit logs' }));
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [opened, entityId, lastLoadedId, loadedAll, loadAuditData, loadEntityAuditData]);

  // Choose which logs to display
  const logsToDisplay: AuditLog[] = entityId
    ? Object.values(entityAuditLogs || {})
    : auditLogs;

  return (
    <AuditLogViewer
      opened={opened}
      onClose={onClose}
      auditLogs={logsToDisplay}
      loading={loading}
      errorMessage={errorMessage}
    />
  );
};

const mapDispatchToProps = (dispatch: Dispatch) => ({actions: bindActionCreators({
    setAuditLogs: auditLogStore.actions.set,
    loadAuditData: loadAllAuditLogState,
    loadEntityAuditData: loadEntityAuditLogState
  }, dispatch)});
const mapStateToProps = (state: CommonState) => ({
  auditLogs: auditLogStore.selectors.getAsArray(state),
  entityAuditLogs: auditLogStore.selectors.getEntityAuditLogs(state),
  usersById: userStore.selectors.getItems(state),
  getUserById: userStore.selectors.getById(state),
  getItemById: itemStore.selectors.getById(state)
});

export default connect(mapStateToProps, mapDispatchToProps)(LoaderComponent);
